import React from 'react';
import styled from 'styled-components';
import { FieldWrapper, Icon } from 'components';
import { theme, toRem } from 'design';
import { EmployeeType, SiteRole } from 'types';

// Sidebar
export const AddedUsersList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  margin: 0;
  padding: 0 ${toRem(theme.layout.gutter.small)} ${toRem(33)};
  list-style: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 ${toRem(theme.layout.gutter.large)} ${toRem(33)};
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    padding: 0 0 ${toRem(33)};
  }
`;

const ListItem = styled.li`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;
  min-height: ${toRem(66)};
  margin-bottom: ${toRem(11)};
  padding: ${toRem(22)} ${toRem(22)};
  border-radius: ${toRem(11)};
  background-color: ${theme.color.white};

  svg {
    min-width: ${toRem(24)};
    min-height: ${toRem(24)};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const AddedUserInfo = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.75)};
  line-height: 125%;
  color: ${theme.color.neutral[900]};
  margin-right: ${toRem(8)};
`;

const Name = styled.span`
  margin-bottom: ${toRem(5.5)};
  font-weight: ${theme.font.weight.medium};
`;

const Label = styled.span`
  color: ${theme.color.neutral[500]};
`;

interface IAddedUserProps {
  name: string;
  type: EmployeeType;
  role: SiteRole;
  jobTitle?: string;
  site?: string;
}

export const AddedUserListItem: React.FC<IAddedUserProps> = ({ name, type, role, jobTitle, site }) => {
  const label = (role: SiteRole) => {
    if (type === EmployeeType.ADMIN) {
      return 'Organization Admin';
    }
    if (role === SiteRole.ADMIN) {
      return `Site Admin, ${site}`;
    }
    if (role === SiteRole.MANAGER) {
      return `Manager, ${site}`;
    }
    if (role === SiteRole.EMPLOYEE) {
      return `${jobTitle}, ${site}`;
    }
    return null;
  };

  return (
    <ListItem data-cy="added-user-item">
      <AddedUserInfo>
        <Name>{name}</Name>
        <Label>{label(role)}</Label>
      </AddedUserInfo>

      <Icon icon="Success" width={24} height={24} fill={theme.color.secondary[400]} />
    </ListItem>
  );
};

// Add User Form
export const Form = styled.form`
  width: 100%;
  padding-top: ${toRem(11)};
`;

export const FormLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(22)};
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: ${toRem(33)} ${toRem(22)};
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    grid-template-columns: repeat(auto-fit, minmax(${toRem(250)}, 1fr));
  }

  @media (min-width: ${theme.breakpoint.xlarge}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StyledFieldWrapper = styled(FieldWrapper)`
  margin-bottom: 0;
`;

export const Footer = styled.footer`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  width: 100%;
  margin: ${toRem(55)} 0;
  padding-top: ${toRem(33)};
  border-top: ${toRem(1)} solid ${theme.color.neutral[200]};
`;
